import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, useRef, useEffect } from 'react';
import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { ArrowRight, Palette, Sparkles, Users, Globe, Trash2, Plus } from 'lucide-react';
import { ImageGallery } from '@/components/glitch-ui/image-gallery';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/ui/button';
import { AddLandingPageModal } from '@/components/assets/add-landing-page-modal';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { PageErrorState } from '@/components/glitch-ui/empty-state';
import { useAtom, useAtomValue } from 'jotai';
import { organizationDataAtom, selectedAssetsTabAtom, selectedAssetsGoogleSubTabAtom, mediaViewModeAtom, googleAdsAccountAtom, googleAdsAccountResolvedAtom } from '@/store';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLandingPagesDetails, deleteLandingPage } from '@/lib/apis';
import type { LandingPageDetails } from '@/lib/apis';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  businessQueryOptions,
  accountSitelinksQueryOptions,
  accountCalloutsQueryOptions,
  negativeKeywordListsQueryOptions,
  accountIpExclusionsQueryOptions,
} from '@/lib/query-options';
import { AccountCallouts, type AccountCalloutsRef } from '@/components/settings/account/account-callouts';
import { AccountSitelinks, type AccountSitelinksRef } from '@/components/settings/account/account-sitelinks';
import { NegativeKeywordLists, NegativeKeywordDetail, type NegativeKeywordListsRef } from '@/components/settings/account/negative-keyword-lists';
import { AccountIpExclusions, type AccountIpExclusionsRef } from '@/components/settings/account/account-ip-exclusions';
import { ConversionManagement } from '@/components/settings/conversion-management';
import { useTestMode } from '@/hooks/useTestMode';
import { withOrgReset } from '@/components/glitch-ui/with-org-reset';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { useToast } from '@/hooks/useToast';

export const Route = createFileRoute('/_authenticated/_layout/assets/')({
  component: withOrgReset(AssetsPage),
  staticData: {
    title: 'Assets',
    icon: Palette,
  },
});

// Tab configuration
const TAB_IDS = ['landing-pages', 'media', 'conversions', 'google', 'audiences'] as const;
type TabId = typeof TAB_IDS[number];

const allTabs = [
  { id: 'landing-pages' as const, label: 'Landing Pages', testModeOnly: false },
  { id: 'media' as const, label: 'Media', testModeOnly: false },
  { id: 'conversions' as const, label: 'Conversions', testModeOnly: false },
  { id: 'google' as const, label: 'Google', testModeOnly: false },
  { id: 'audiences' as const, label: 'Audiences', testModeOnly: true },
];


// Helper to extract page title from URL
function getPageTitle(url: string): string {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    // If it's the root, return "Home"
    if (path === '/' || path === '') {
      return 'Home';
    }

    // Get the last segment of the path
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || 'Home';

    // Convert slug to title case (e.g., "about-us" -> "About Us")
    return lastSegment
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  } catch {
    return 'Page';
  }
}

// Helper to generate screenshot URL using a thumbnail service
function getScreenshotUrl(url: string): string {
  // Using a free screenshot service - you could replace with your own
  return `https://api.microlink.io/?url=${encodeURIComponent(url)}&screenshot=true&meta=false&embed=screenshot.url`;
}

// #4526 — a landing page can't be removed while it's referenced. Returns true when any ad,
// sitelink, or product points at the URL.
function isLandingPageReferenced(lp: LandingPageDetails): boolean {
  return lp.ad_count > 0 || lp.sitelink_count > 0 || (lp.product_count ?? 0) > 0;
}

function landingPageReferenceMessage(lp: LandingPageDetails): string {
  const parts: string[] = [];
  if (lp.ad_count > 0) parts.push(`${lp.ad_count} ad${lp.ad_count === 1 ? '' : 's'}`);
  if (lp.sitelink_count > 0) parts.push(`${lp.sitelink_count} sitelink${lp.sitelink_count === 1 ? '' : 's'}`);
  const productCount = lp.product_count ?? 0;
  if (productCount > 0) parts.push(`${productCount} product${productCount === 1 ? '' : 's'}`);
  return `Deletion is blocked while this URL is used by ${parts.join(', ')}.`;
}

function AssetsPage() {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [selectedNegativeListId, setSelectedNegativeListId] = useState<string | null>(null);
  const [deleteUrl, setDeleteUrl] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [failedThumbnailUrls, setFailedThumbnailUrls] = useState<Set<string>>(new Set());
  // Toolbar handed up from ImageGallery so the media controls sit inline with the tab bar.
  const [mediaControls, setMediaControls] = useState<React.ReactNode>(null);
  const mediaViewMode = useAtomValue(mediaViewModeAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const testMode = useTestMode();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Google sub-tabs mirror the campaign Misc tab order: Sitelinks → Callouts → Negatives.
  // Callouts is the only one available outside test mode.
  type GoogleSubTab = 'sitelinks' | 'callouts' | 'negatives' | 'ip-exclusions';
  const GOOGLE_SUB_TABS: GoogleSubTab[] = ['sitelinks', 'callouts', 'negatives', 'ip-exclusions'];
  const [savedGoogleSubTab, setSavedGoogleSubTab] = useAtom(selectedAssetsGoogleSubTabAtom);
  const [googleSubTab, setGoogleSubTab] = useState<GoogleSubTab>(() => {
    const initial = (window.location.hash || '').replace('#', '').split('/');
    if (initial[0] === 'google' && GOOGLE_SUB_TABS.includes(initial[1] as GoogleSubTab)) {
      return initial[1] as GoogleSubTab;
    }
    return GOOGLE_SUB_TABS.includes(savedGoogleSubTab as GoogleSubTab) ? savedGoogleSubTab as GoogleSubTab : 'callouts';
  });
  // Section refs let the hoisted Add button trigger each section's existing add-modal logic.
  const accountSitelinksRef = useRef<AccountSitelinksRef>(null);
  const accountCalloutsRef = useRef<AccountCalloutsRef>(null);
  const accountNegativesRef = useRef<NegativeKeywordListsRef>(null);
  const accountIpExclusionsRef = useRef<AccountIpExclusionsRef>(null);
  // Counts for the tab badges — reuse the shared account-asset query options so these share the
  // section components' cache (no duplicate fetches) and refetch on account switch the same way.
  const accountId = googleAdsAccount?.googleads_account_id;
  const googleAdsAccountResolved = useAtomValue(googleAdsAccountResolvedAtom);
  const { data: accountSitelinksData, isPending: sitelinksPending } = useQuery(accountSitelinksQueryOptions(accountId));
  const { data: accountCalloutsData, isPending: calloutsPending } = useQuery(accountCalloutsQueryOptions(accountId));
  const { data: accountNegativesData, isPending: negativesPending } = useQuery(negativeKeywordListsQueryOptions(accountId));
  const { data: accountIpExclusionsData, isPending: ipExclusionsPending } = useQuery(accountIpExclusionsQueryOptions(accountId));
  // While the account atom hydrates (queries disabled, isPending true with no fetch) or the
  // four Google queries load, the tab must show a loading state — the counts all read 0
  // during that window, which flashed the "No Google Assets" empty state on boot (#5820).
  const googleDataPending =
    !googleAdsAccountResolved ||
    (!!accountId && (sitelinksPending || calloutsPending || negativesPending || ipExclusionsPending));

  // Filter tabs based on test mode
  const tabs = useMemo(() => {
    return allTabs.filter(tab => !tab.testModeOnly || testMode);
  }, [testMode]);

  const visibleTabIds = tabs.map(t => t.id) as TabId[];
  // Hash format: `<tab>` or `<tab>/<sub-tab>` — strip the sub-tab before passing to the hook.
  const [hashMain, hashSubTab] = (location.hash?.replace('#', '') || '').split('/');
  const hashForHook = hashMain ? `#${hashMain}` : '';
  const { activeTab, handleTabChange } = useTabPersistence(
    selectedAssetsTabAtom,
    visibleTabIds,
    'landing-pages',
    (tabId, replace) => navigate({ to: '/assets', hash: tabId, replace }),
    hashForHook || location.hash,
  );

  // Keep `googleSubTab` in sync with the URL when navigating between hashes inside the app
  // (the useState lazy init above only covers fresh page loads).
  useEffect(() => {
    if (hashMain === 'google' && hashSubTab && GOOGLE_SUB_TABS.includes(hashSubTab as GoogleSubTab)) {
      setGoogleSubTab(hashSubTab as GoogleSubTab);
      setSavedGoogleSubTab(hashSubTab as GoogleSubTab);
    }
  }, [hashMain, hashSubTab]);

  // Fetch business data for the current organization
  const { data: business, isError: isBusinessError, isPending: isBusinessPending, refetch: refetchBusiness } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Fetch landing pages details with scraped data
  const { data: landingPagesDetails = [], isLoading: isLandingPagesLoading } = useQuery({
    queryKey: ['landingPagesDetails', business?.id],
    queryFn: () => {
      if (!business?.id) return Promise.resolve([]);
      return getLandingPagesDetails(business.id);
    },
    enabled: !!business?.id,
  });

  const isLoadingLandingPages = !!organizationData?.id && (isBusinessPending || isLandingPagesLoading);

  // Set of onboarding link URLs (only these can be deleted)
  const onboardingLinks = useMemo(
    () => new Set(business?.onboarding_links || []),
    [business?.onboarding_links],
  );

  const deleteMutation = useMutation({
    mutationFn: (url: string) => deleteLandingPage(business!.id, url),
    onSuccess: () => {
      posthog?.capture('landing_page_deleted');
      queryClient.invalidateQueries({ queryKey: ['landingPagesDetails', business?.id] });
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationData?.id, 'business'] });
      if (selectedUrl === deleteUrl) setSelectedUrl(null);
      setDeleteUrl(null);
    },
    onError: () => {
      toast({ description: 'Failed to delete landing page', variant: 'destructive' });
    },
  });

  // Get selected landing page details
  const selectedLandingPage = selectedUrl
    ? landingPagesDetails.find(lp => lp.url === selectedUrl)
    : null;

  // Negatives uses the same master-detail layout as landing pages: the list lives in the card,
  // its keywords render in a sibling column outside the card. Only when lists exist (otherwise the
  // Google tab shows its single empty state).
  const negativeListCount = accountNegativesData?.lists?.length ?? 0;
  const isNegativesDetail = activeTab === 'google' && googleSubTab === 'negatives' && negativeListCount > 0;

  if (isBusinessError) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3"></div>
        </div>
        <div className="flex-1">
          <PageErrorState
            title="Couldn't Load Assets"
            description="This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists."
            onRetry={() => refetchBusiness()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3">
        </div>
      </div>

      <div className="flex-1 space-y-6">
        {/* Landing Pages with data: 9/12 + 3/12 Grid layout */}
        {activeTab === 'landing-pages' && landingPagesDetails.length > 0 ? (
          <div className="grid grid-cols-12 gap-1">
            {/* Left: Landing Pages List (9/12) */}
            <div className="col-span-9">
              <TabCardPanel
                tabs={tabs.map((tab) => ({
                  id: tab.id,
                  label: tab.label,
                  isActive: activeTab === tab.id,
                  onClick: () => handleTabChange(tab.id),
                }))}
                noPadding
                actions={
                  activeTab === 'landing-pages' && business ? (
                    <Button variant="outline" className="text-gray-900 h-9" onClick={() => setIsAddModalOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  ) : undefined
                }
              >
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                          </th>
                          <th className="pl-0 pr-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Page Title
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            Sitelinks
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            Ads
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            Products
                          </th>
                          <th className="w-12" />
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {landingPagesDetails.map((landingPage) => (
                          <tr
                            key={landingPage.url}
                            className={`hover:bg-gray-50 cursor-pointer ${selectedUrl === landingPage.url ? 'bg-[#290BD9]/10' : ''}`}
                            onClick={() => setSelectedUrl(landingPage.url)}
                          >
                            {/* Landing Page Thumbnail */}
                            <td className="px-6 py-4 whitespace-nowrap w-36">
                              <a
                                href={landingPage.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className={`block w-24 h-16 rounded-md overflow-hidden border border-gray-200 bg-gray-100 hover:border-[#290BD9] transition-colors cursor-pointer ${failedThumbnailUrls.has(landingPage.url) ? 'flex items-center justify-center' : ''}`}
                              >
                                {failedThumbnailUrls.has(landingPage.url) ? (
                                  // Placeholder shown when the screenshot fails to load
                                  <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path></svg>
                                ) : (
                                  <img
                                    src={getScreenshotUrl(landingPage.url)}
                                    alt={`Preview of ${getPageTitle(landingPage.url)}`}
                                    className="w-full h-full object-cover object-top"
                                    onError={() => {
                                      setFailedThumbnailUrls((prev) => new Set(prev).add(landingPage.url));
                                    }}
                                  />
                                )}
                              </a>
                            </td>

                            {/* Page Title */}
                            <td className="pl-0 pr-6 py-4">
                              <div className="text-base font-medium text-gray-900">
                                {landingPage.title || getPageTitle(landingPage.url)}
                              </div>
                              <a
                                href={landingPage.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm text-gray-500 hover:text-[#290BD9] transition-colors block max-w-xs truncate"
                                title={landingPage.url}
                              >
                                {landingPage.url}
                              </a>
                            </td>

                            {/* Sitelinks */}
                            <td className="px-6 py-4 text-right">
                              <div className="text-sm text-gray-900">
                                {landingPage.sitelink_count > 0 ? landingPage.sitelink_count : '-'}
                              </div>
                            </td>

                            {/* Ads */}
                            <td className="px-6 py-4 text-right">
                              <div className="text-sm text-gray-900">
                                {landingPage.ad_count > 0 ? landingPage.ad_count : '-'}
                              </div>
                            </td>

                            {/* Products */}
                            <td className="px-6 py-4 text-right">
                              <div className="text-sm text-gray-900">
                                {landingPage.product_count > 0 ? landingPage.product_count : '-'}
                              </div>
                            </td>

                            {/* Delete */}
                            <td className="px-3 py-4">
                              {onboardingLinks.has(landingPage.url) && onboardingLinks.size > 1 && landingPagesDetails.length > 1 && (
                                isLandingPageReferenced(landingPage) ? (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                                          <button type="button" disabled className="p-1 rounded text-gray-300 cursor-not-allowed">
                                            <Trash2 className="h-4 w-4" />
                                          </button>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="max-w-[260px] text-xs">{landingPageReferenceMessage(landingPage)}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteUrl(landingPage.url);
                                    }}
                                    className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Delete landing page"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              </TabCardPanel>
            </div>

            {/* Right: Details Panel (3/12) */}
            <div className="col-span-3">
              <div className="sticky top-0 mt-[42px]">
                {selectedLandingPage ? (
                  <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4 space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedLandingPage.title || getPageTitle(selectedLandingPage.url)}
                      </h3>
                      <a
                        href={selectedLandingPage.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#290BD9] hover:underline break-all"
                      >
                        {selectedLandingPage.url}
                      </a>
                    </div>

                    {selectedLandingPage.description && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="h-4 w-4 text-[#290BD9]" />
                          <span className="text-xs text-gray-700 font-medium">AI understands</span>
                        </div>
                        <p className="text-sm text-gray-900">
                          {selectedLandingPage.description}
                        </p>
                      </div>
                    )}

                    {selectedLandingPage.intent && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="h-4 w-4 text-[#290BD9]" />
                          <span className="text-xs text-gray-700 font-medium">AI thinks</span>
                        </div>
                        <p className="text-sm text-gray-900">
                          {selectedLandingPage.intent}
                        </p>
                      </div>
                    )}

                    {!selectedLandingPage.description && !selectedLandingPage.intent && (
                      <p className="text-sm text-gray-500">
                        No AI analysis available for this landing page yet.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                    <Globe className="mx-auto h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                    <h3 className="text-lg font-semibold text-gray-500 mb-2">Select a Landing Page</h3>
                    <p className="text-gray-500">
                      Click any row in the table to view AI analysis
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Other tabs - full width, except Negatives which uses a 6/6 master-detail grid
             (list in the card, keywords in a sibling column outside the card). */
          <div className={isNegativesDetail ? 'grid grid-cols-12 gap-1' : ''}>
          <div className={isNegativesDetail ? 'col-span-8 min-w-0' : ''}>
          <TabCardPanel
            tabs={tabs.map((tab) => ({
              id: tab.id,
              label: tab.label,
              isActive: activeTab === tab.id,
              onClick: () => handleTabChange(tab.id),
            }))}
            noPadding={activeTab === 'media' && mediaViewMode === 'table'}
            actions={
              activeTab === 'landing-pages' && business ? (
                <Button variant="outline" className="text-gray-900 h-9" onClick={() => setIsAddModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              ) : activeTab === 'media' ? (
                mediaControls ?? undefined
              ) : undefined
            }
          >
                {/* Landing Pages Tab (loading / empty state) */}
                {activeTab === 'landing-pages' && (
                  isLoadingLandingPages ? (
                    <div className="flex items-center justify-center gap-3 min-h-[50vh]">
                      <Spinner size="md" className="text-gray-500" />
                      <span className="text-gray-500">Loading landing pages...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                      <Globe className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                      <h3 className="text-lg font-semibold text-gray-500 mb-2">No Landing Pages</h3>
                      <p className="text-gray-500">Landing pages will automatically be detected after you create your business</p>
                    </div>
                  )
                )}

                {/* Media Tab */}
                {activeTab === 'media' && (
                  <ImageGallery mode='library' onControlsReady={setMediaControls} />
                )}

                {/* Conversions Tab */}
                {activeTab === 'conversions' && (
                  <ConversionManagement />
                )}

                {/* Google Tab — one sub-tab per account-level asset section. Order mirrors the
                    campaign Misc tab: Sitelinks → Callouts → Negatives. Each tab
                    label carries a purple count badge; the Add button is hoisted to the right of
                    the tabs and changes label per active sub-tab. */}
                {activeTab === 'google' && (() => {
                  const sitelinkCount = accountSitelinksData?.sitelinks?.length ?? 0;
                  const calloutCount = accountCalloutsData?.callouts?.length ?? 0;
                  const negativeCount = accountNegativesData?.lists?.length ?? 0;
                  const ipExclusionCount = accountIpExclusionsData?.exclusions?.length ?? 0;
                  type GoogleSubTabConfig = {
                    id: GoogleSubTab;
                    label: string;
                    count: number;
                    max: number | null;
                    addLabel: string;
                    triggerAdd: () => void;
                  };
                  const googleSubTabs: GoogleSubTabConfig[] = [
                    { id: 'sitelinks' as GoogleSubTab, label: 'Sitelinks', count: sitelinkCount, max: 20, addLabel: 'Add Sitelink', triggerAdd: () => accountSitelinksRef.current?.openAdd() },
                    { id: 'callouts', label: 'Callouts', count: calloutCount, max: 20, addLabel: 'Add Callout', triggerAdd: () => accountCalloutsRef.current?.openAdd() },
                    { id: 'negatives' as GoogleSubTab, label: 'Negative Lists', count: negativeCount, max: 20, addLabel: 'New List', triggerAdd: () => accountNegativesRef.current?.openAdd() },
                    { id: 'ip-exclusions' as GoogleSubTab, label: 'IP Exclusions', count: ipExclusionCount, max: null, addLabel: 'Add IPs', triggerAdd: () => accountIpExclusionsRef.current?.openAdd() },
                  ];
                  // Hide sub-tabs entirely until at least one section has content.
                  // First-time-empty surface gets a single tall empty state (no
                  // partial table rows under empty tab strips).
                  const totalCount = sitelinkCount + calloutCount + negativeCount + ipExclusionCount;
                  if (googleDataPending) {
                    return (
                      <div className="flex items-center justify-center gap-3 min-h-[50vh]">
                        <Spinner size="md" className="text-gray-500" />
                        <span className="text-gray-500">Loading Google assets...</span>
                      </div>
                    );
                  }
                  if (totalCount === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                        <Globe className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                        <h3 className="text-lg font-semibold text-gray-500 mb-2">No Google Assets</h3>
                        <p className="text-gray-500 mb-6">Sitelinks, callouts, negative keywords, and IP exclusions will appear here once they're added</p>
                        <Button
                          onClick={() => navigate({ to: '/settings/integrations' as string })}
                          size="lg"
                          className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                        >
                          <span>SELECT ACCOUNT</span>
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  }
                  const activeSubTab = googleSubTabs.some((t) => t.id === googleSubTab) ? googleSubTab : 'callouts';
                  const activeConfig = googleSubTabs.find((t) => t.id === activeSubTab);
                  const isAddDisabled = activeConfig?.max != null && activeConfig.count >= activeConfig.max;
                  return (
                    <div className="space-y-6">
                      {/* -mt-2.5 cancels the sub-tab buttons' top padding so the space above the
                          tab text matches the space below the divider (#4812). */}
                      <div className="-mt-2.5 border-b border-gray-200 flex items-end justify-between">
                        <div className="flex gap-0">
                          {googleSubTabs.map((tab) => {
                            const isActive = activeSubTab === tab.id;
                            return (
                              <button
                                key={tab.id}
                                type="button"
                                onClick={() => { setGoogleSubTab(tab.id); setSavedGoogleSubTab(tab.id); }}
                                className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                                  isActive
                                    ? 'border-[#290BD9] text-gray-900'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                              >
                                {tab.label}
                              </button>
                            );
                          })}
                        </div>
                        {activeConfig && (
                          <div className="pb-1.5">
                            <Button
                              variant="outline"
                              className="text-gray-900"
                              onClick={activeConfig.triggerAdd}
                              disabled={isAddDisabled}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {activeConfig.addLabel}
                            </Button>
                          </div>
                        )}
                      </div>

                      {activeSubTab === 'sitelinks' && <AccountSitelinks ref={accountSitelinksRef} />}
                      {activeSubTab === 'callouts' && <AccountCallouts ref={accountCalloutsRef} />}
                      {activeSubTab === 'negatives' && (
                        <NegativeKeywordLists
                          ref={accountNegativesRef}
                          selectedListId={selectedNegativeListId}
                          onSelectList={setSelectedNegativeListId}
                        />
                      )}
                      {activeSubTab === 'ip-exclusions' && <AccountIpExclusions ref={accountIpExclusionsRef} />}
                    </div>
                  );
                })()}

                {/* Audiences Tab */}
                {activeTab === 'audiences' && (
                  <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                    <Users className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                    <h3 className="text-lg font-semibold text-gray-500 mb-2">Audience Lists</h3>
                    <p className="text-gray-500">Coming soon</p>
                  </div>
                )}
          </TabCardPanel>
          </div>
          {isNegativesDetail && (
            <div className="col-span-4 min-w-0">
              <div className="sticky top-0 mt-[42px]">
                <NegativeKeywordDetail listId={selectedNegativeListId} />
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      {business && (
        <AddLandingPageModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          businessId={business.id}
          organizationId={organizationData?.id ?? 0}
          existingUrls={landingPagesDetails.map(lp => lp.url)}
        />
      )}

      <ConformationModal
        isOpen={!!deleteUrl}
        onClose={() => setDeleteUrl(null)}
        onConfirm={() => deleteUrl && deleteMutation.mutate(deleteUrl)}
        title="Delete Landing Page"
        description={`Are you sure you want to remove this landing page? Existing ads using this URL will not be affected.`}
        confirmText="Delete"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
